import { isEmpty, isNil } from 'lodash';
import { useCallback } from 'react';
import { useControlsState } from '@shared/components/filter/state/ControlsProvider';
import { isTextComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';

type CardMatchesSearchQueryHandler = (
  entity: API.WorkflowEntity,
  refs: NonNullable<API.ListWorkflowEntitiesQuery['list']>['refs'] | null,
) => boolean;

export function useCardMatchesSearchQuery(): CardMatchesSearchQueryHandler {
  const { search } = useControlsState();

  return useCallback(
    (
      entity: API.WorkflowEntity,
      refs: NonNullable<API.ListWorkflowEntitiesQuery['list']>['refs'] | null,
    ) => {
      if (isNil(search) || isEmpty(search)) {
        return true;
      }

      const fields = entity.fields || {};
      const searchLower = search.toLowerCase();

      // Find the workflow template for this entity
      const workflowTemplate = refs?.workflowTemplateRefs?.find(
        t => t.id === entity.workflowTemplateId,
      );

      if (isNil(workflowTemplate)) {
        return false;
      }

      // Check if any text component field matches the search query
      return Object.keys(fields).some(fieldId => {
        const component = workflowTemplate?.components?.find(c => c.id === fieldId);

        if (!component || !isTextComponent(component)) {
          return false; // Skip non-text components or if component not found
        }

        const fieldValue = fields[fieldId];
        const textValue = String(fieldValue || '').toLowerCase();
        return textValue.includes(searchLower);
      });
    },
    [search],
  );
}
